import { summarizeForwardBuyByCart } from '../utils/forwardBuySummary';
import { useCallback, useMemo } from 'react';
import type {
  AssignmentLine,
  CartAssignment,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { filterAssignmentLines } from '../utils/lineFilters';
import {
  applyFocusFilters,
  buildCartCreatePlan,
  computeCartStats,
  dedupeCarts,
  filterByDesignation,
  forwardBoughtShipToSet,
  groupLinesByCart,
  hubOptions,
  indexCartsByShipTo,
  shipToOptions,
  sortRailCarts,
  warehouseOptions,
  type DesignationFilter,
} from '../utils/assignmentCarts';
import {
  useAssignmentUrlFilters,
  useFocusPickerState,
} from '../utils/useAssignmentBoardFilters';

interface ModelInput {
  carts: CartAssignment[] | undefined;
  lines: AssignmentLine[];
  designationFilter: DesignationFilter | undefined;
  selectedIds: Set<string>;
  supplierId: string;
}

/**
 * Everything the supplier board derives from its loaded lines: carts, the
 * rail's per-cart stats and create plans, and the toolbar-filtered line set.
 */
export function useAssignmentModel({
  carts: rawCarts,
  lines,
  designationFilter = 'all',
  selectedIds,
  supplierId,
}: ModelInput) {
  const scopedLines = useMemo(
    () => filterByDesignation(lines, designationFilter),
    [lines, designationFilter],
  );
  const carts = useMemo(() => dedupeCarts(rawCarts ?? []), [rawCarts]);
  const cartsByShipTo = useMemo(() => indexCartsByShipTo(carts), [carts]);

  // Lines grouped by their PO cart (ship-to), pre-toolbar-filter. Drives the
  // MOV rail's per-cart stats + the create summary (you create the whole cart,
  // not the filtered subset). MOV %s stay honest against the full denominator.
  const scopedLinesByCart = useMemo(
    () => groupLinesByCart(carts, scopedLines),
    [carts, scopedLines],
  );

  // Read from every line, not the designation-scoped ones: the removal acts on
  // the whole cart whatever the table is filtered to.
  const forwardBoughtShipTos = useMemo(
    () => forwardBoughtShipToSet(lines),
    [lines],
  );
  // Forward buying is a fact about the whole cart, so it reads every line, not
  // the designation-scoped ones.
  const forwardBuyByCart = summarizeForwardBuyByCart(carts, lines);
  const cartStats = useMemo(
    () => computeCartStats(carts, scopedLinesByCart, selectedIds),
    [carts, scopedLinesByCart, selectedIds],
  );
  const railCarts = useMemo(
    () => sortRailCarts(carts, cartStats),
    [carts, cartStats],
  );
  const planForCart = useCallback(
    (shipToWarehouseId: string) =>
      buildCartCreatePlan(
        scopedLinesByCart.get(shipToWarehouseId) ?? [],
        selectedIds,
        cartsByShipTo.get(shipToWarehouseId),
      ),
    [scopedLinesByCart, selectedIds, cartsByShipTo],
  );

  const url = useAssignmentUrlFilters();
  const focus = useFocusPickerState(supplierId);

  // Options come from the view-scoped lines so the pickers only offer ship-tos
  // / warehouses / hubs actually present on this supplier's board.
  const shipToFilterOptions = useMemo(
    () => shipToOptions(scopedLines, cartsByShipTo),
    [scopedLines, cartsByShipTo],
  );
  const warehouseFilterOptions = useMemo(
    () => warehouseOptions(scopedLines),
    [scopedLines],
  );
  const hubFilterOptions = useMemo(
    () => hubOptions(scopedLines),
    [scopedLines],
  );

  // Final render set: toolbar (search + chips), then the rail's ship-to scope
  // and the warehouse/hub focus filters.
  const toolbarFiltered = useMemo(
    () =>
      filterAssignmentLines({
        lines: scopedLines,
        carts,
        search: url.search,
        chips: url.chips,
        // So the `<MOV` / `<Freight` chips measure the same selected buy side
        // the rail gauge and Create do.
        selectedIds,
      }),
    [scopedLines, carts, url.search, url.chips, selectedIds],
  );
  const { selectedShipTo } = url;
  const { shipToIds, warehouseIds, hubNames } = focus;
  const filteredLines = useMemo(
    () =>
      applyFocusFilters(toolbarFiltered, {
        selectedShipTo,
        shipToIds,
        warehouseIds,
        hubNames,
      }),
    [toolbarFiltered, selectedShipTo, shipToIds, warehouseIds, hubNames],
  );

  return {
    cartsByShipTo,
    scopedLines,
    forwardBoughtShipTos,
    forwardBuyByCart,
    cartStats,
    railCarts,
    planForCart,
    filteredLines,
    filters: {
      url,
      shipTo: {
        options: shipToFilterOptions,
        value: focus.shipToFilter,
        onChange: focus.setShipToFilter,
      },
      warehouse: {
        options: warehouseFilterOptions,
        value: focus.warehouseFilter,
        onChange: focus.setWarehouseFilter,
      },
      hub: {
        options: hubFilterOptions,
        value: focus.hubFilter,
        onChange: focus.setHubFilter,
      },
    },
  };
}
